/**
 * The input side of the product: dive conditions, not camera settings.
 *
 * Divers do not think in tokens. They think "36 m wreck, green water, vis about
 * 6 m, no lights". Everything the UI collects is in this shape; the engine is
 * the only place that knows about `$WARM`.
 */

import type { Camera } from "./cameras";

export const WATER_TYPES = [
  "tropical_salt",
  "temperate_salt",
  "lake_clear",
  "lake_green",
] as const;
export type WaterType = (typeof WATER_TYPES)[number];

export const VISIBILITIES = ["high", "medium", "low"] as const;
export type Visibility = (typeof VISIBILITIES)[number];

export const LIGHT_SOURCES = ["ambient", "mixed", "video_lights"] as const;
export type LightSource = (typeof LIGHT_SOURCES)[number];

export const WORKFLOWS = ["graded", "straight_out"] as const;
export type Workflow = (typeof WORKFLOWS)[number];

/** GoPro Labs' `x` command only documents half-stop increments (x0, x-.5,
 *  x-1, x-1.5, x-2, …) — there is no third- or fifth-stop granularity to
 *  offer, however finely a diver might normally dial it in. Curated down to
 *  the three values divers actually reach for underwater, where GoPro's own
 *  metering tends to overexpose: -1/2 EV covers most cinematic shots, -1 EV
 *  is the next step down for especially bright, shallow water. */
export const EV_COMP_VALUES = [-1, -0.5, 0] as const;
export type EvComp = (typeof EV_COMP_VALUES)[number];

/** How the extension tokens ($WBLK, $WARM, etc.) behave once scanned:
 *  - "session": `$FOURCC=value` — resets at the next power-off.
 *  - "permanent": `*FOURCC=value` — survives power-off, until changed.
 *  - "dial": wraps them in an on-select/on-deselect macro pair tied to a
 *    named preset (see `buildAdvancedTokens` in engine.ts), so they follow
 *    whichever preset is selected on the camera's dial instead of freezing
 *    at whatever was last scanned. Costs a 4-letter dial code instead of a
 *    friendly name — see engine.ts and compiler.ts's `compileAdvanced`,
 *    which packs both macros and the preset into one scanned code. Confirmed
 *    necessary by hardware testing: neither "session" nor "permanent"
 *    settings are re-applied by selecting a different preset from the
 *    physical dial — only "dial" mode is. */
export const PERSISTENCE_MODES = ["session", "permanent", "dial"] as const;
export type PersistenceMode = (typeof PERSISTENCE_MODES)[number];

export const SUBJECTS = ["wide", "macro", "wreck", "surface"] as const;
export type Subject = (typeof SUBJECTS)[number];

export const FRAME_RATES = [24, 30, 60] as const;
export type FrameRate = (typeof FRAME_RATES)[number];

export const RESOLUTIONS = ["5.3k", "4k"] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export const ASPECT_RATIOS = ["8:7", "16:9", "4:3"] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

/** Which aspect ratios exist as a Labs `r` token at each resolution. Both
 *  resolutions cover all three today (r4/r4T/r4X, r5/r5T/r5X) — kept as a
 *  per-resolution map rather than one flat list because that has not always
 *  been true and GoPro adds sensor-specific modes per camera generation. */
export const ASPECTS_BY_RESOLUTION: Record<Resolution, readonly AspectRatio[]> = {
  "5.3k": ["16:9", "4:3", "8:7"],
  "4k": ["16:9", "4:3", "8:7"],
};

export const MAX_DEPTH_M = 60;

export interface DiveConditions {
  camera: Camera;
  water: WaterType;
  /** Metres. The single most consequential input, which is why it gets the
   *  water column rather than a number field. */
  depthM: number;
  visibility: Visibility;
  light: LightSource;
  workflow: Workflow;
  subject: Subject;
  /** Exposure compensation, in stops. See `EV_COMP_VALUES`. */
  evComp: EvComp;
  frameRate: FrameRate;
  resolution: Resolution;
  aspect: AspectRatio;
  /** Creates a named preset on the camera dial (HERO12/13, MAX2). */
  presetName?: string;
  /** The icon shown on the camera's preset dial next to `presetName`. One of
   *  `PRESET_ICON_VALUES`; only meaningful alongside a `presetName`. */
  presetIcon?: number;
  /** See `PersistenceMode` above. Does not touch $PRES itself, which already
   *  outlasts a power cycle regardless of this choice. */
  persistenceMode: PersistenceMode;
}

export const DEFAULT_CONDITIONS: DiveConditions = {
  camera: "HERO13",
  water: "tropical_salt",
  depthM: 14,
  visibility: "high",
  light: "ambient",
  workflow: "graded",
  subject: "wide",
  evComp: -0.5,
  frameRate: 30,
  resolution: "5.3k",
  aspect: "8:7",
  persistenceMode: "session",
};

export const WATER_LABELS: Record<WaterType, string> = {
  tropical_salt: "Tropical sea",
  temperate_salt: "Temperate sea",
  lake_clear: "Clear lake",
  lake_green: "Green lake",
};

export const VISIBILITY_LABELS: Record<Visibility, string> = {
  high: "Good",
  medium: "Fair",
  low: "Poor",
};

export const LIGHT_LABELS: Record<LightSource, string> = {
  ambient: "Ambient",
  mixed: "Mixed",
  video_lights: "Video lights",
};

export const WORKFLOW_LABELS: Record<Workflow, string> = {
  graded: "Grade it later",
  straight_out: "Share as shot",
};

export const EV_COMP_LABELS: Record<EvComp, string> = {
  [-1]: "-1 EV",
  [-0.5]: "-1/2 EV",
  [0]: "0 EV",
};

export const PERSISTENCE_MODE_LABELS: Record<PersistenceMode, string> = {
  session: "Until power off",
  permanent: "Permanent",
  dial: "Tied to this preset",
};

/**
 * The icon set for `$PRES`. Labels here are the accessible name and the
 * picker's captions; the glyphs themselves are original line drawings in
 * `PresetIconGlyph.tsx`, not a reproduction of GoPro's own bitmap — same
 * concepts (a mountain, a bee, a water drop), drawn from scratch.
 *
 * GoPro's own generator (gopro.github.io/labs/control/custom/) offers a 6x6
 * icon grid, 0-32. Capped at 22 here: an older Labs release note documented
 * "0-22 for icon selection" before a later firmware round added a
 * visually-distinct "HERO13 Bonus Icons" block for 23-32 with no camera
 * matrix in the primary docs to confirm against — the same reasoning
 * `CAPABILITIES` applies everywhere else, so the unconfirmed half is left out
 * rather than risking an icon that silently does not show up on a HERO12 or
 * MAX2 dial. Revisit against a real camera if the missing ten start to
 * matter.
 */
export const PRESET_ICON_VALUES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
] as const;

export const PRESET_ICON_LABELS: Record<(typeof PRESET_ICON_VALUES)[number], string> = {
  0: "Sliders",
  1: "Waves",
  2: "Bicycle",
  3: "Shaka",
  4: "House",
  5: "Speed",
  6: "Trail",
  7: "Sun",
  8: "Dual screen",
  9: "Portrait",
  10: "Target",
  11: "Snowflake",
  12: "Mountain",
  13: "Airplane",
  14: "Water drop",
  15: "Loop",
  16: "Sparkle",
  17: "Running",
  18: "Upload",
  19: "Sprout",
  20: "City",
  21: "Signal",
  22: "Bee",
};

/** Every dive here starts in the water either way. */
export const DEFAULT_PRESET_ICON = 1;

export const SUBJECT_LABELS: Record<Subject, string> = {
  wide: "Wide",
  macro: "Macro",
  wreck: "Wreck",
  surface: "Shallow",
};

/**
 * One line of conditions, for the printed slate and for the shelf.
 * Deliberately terse: on paper, at a boat rail, in the wet.
 */
export function summarise(c: DiveConditions): string {
  return [
    `${c.depthM} m`,
    WATER_LABELS[c.water].toLowerCase(),
    `${VISIBILITY_LABELS[c.visibility].toLowerCase()} vis`,
    LIGHT_LABELS[c.light].toLowerCase(),
    SUBJECT_LABELS[c.subject].toLowerCase(),
  ].join(" · ");
}

/** A default title for a captured preset, so a slot is never nameless. */
export function suggestTitle(c: DiveConditions): string {
  // If the diver already named this for the camera dial, that is the name they
  // will be looking for at the boat rail. Inventing a second one to print
  // beside it would be the app disagreeing with itself.
  const named = c.presetName?.trim();
  if (named) return named.slice(0, 40);

  const noun =
    c.subject === "wreck"
      ? "Wreck"
      : c.subject === "macro"
        ? "Macro"
        : c.subject === "surface"
          ? "Shallow"
          : c.water === "lake_green" || c.water === "lake_clear"
            ? "Lake"
            : "Reef";
  return `${noun} ${c.depthM} m`;
}
